import Chip from "@mui/material/Chip";
import { useEffect, useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { RxPencil1 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import {
  setIsTableLoading,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
} from "src/redux/slices/AccountSlice";
import { RootState } from "src/redux/store";
import {
  useDeleteAccountMutation,
  useGetAccountsQuery,
} from "src/services/AccountServices";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import AddAccountFormWrapper from "../Add/AddAccountFormWrapper";
import EditAccountFormWrapper from "../Edit/EditAccountFormWrapper";
import AccountListing from "./AccountListing";
import { UserModuleActionTypes, UserModuleNameTypes } from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const AccountListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.account);

  const [isOpenAddFrom, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [deleteAccount] = useDeleteAccountMutation();
  const { data, isLoading, isFetching } = useGetAccountsQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["accountName", "openingBalance", "branchId", "accountDetails"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const columns: columnTypes[] = [
    {
      field: "accountName",
      headerName: "Account Name",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },
    {
      field: "openingBalance",
      headerName: "Opening Balance",
      flex: "flex-[.75_.75_0%]",
      extraClasses: "",
      renderCell: (row: any) => <div> &#8377; {row?.openingBalance} </div>,
    },
    {
      field: "branchId",
      headerName: "Branch Name",
      flex: "flex-[1.5_1.5_0%]",
      extraClasses: "flex-wrap",
      renderCell: (row: any) => {
        return (
          <div className=" flex flex-wrap w-full py-1">
            {row?.allBranches ? (
              <Chip
                label={"All Branches"}
                className="me-2 my-1"
              />
            ) : (
              row?.branchId?.map((branch: any) => {
                return (
                  <Chip
                    key={branch?._id}
                    label={branch?.branchName}
                    className="me-2 my-1"
                  />
                );
              })
            )}
          </div>
        );
      },
    },
    {
      field: "accountDetails",
      headerName: "Account Details",
      flex: "flex-[1_1_0%]",
      extraClasses: "min-w-[150px]",
      renderCell: (row: any) => <div>{row?.accountDetails} </div>,
    },
    {
      field: "action",
      headerName: "Action",
      flex: "flex-[0.25_0.25_0%]",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  const getActionOptions = (accountId: string) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setSelectedAccountId(accountId);
          setIsOpenEditForm(true);
        },
        moduleName: UserModuleNameTypes.ACCOUNTS,
        actionName: UserModuleActionTypes.EDIT,
      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure, you want to Delete this Account ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteAccount(accountId).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        moduleName: UserModuleNameTypes.ACCOUNTS,
        actionName: UserModuleActionTypes.DELETE,
      },
    ];
  };

  return (
    <div>
      <AccountListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.ACCOUNTS,
          actionName: UserModuleActionTypes.LIST,
          columns: columns
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />

      {isOpenAddFrom ? (
        <AddAccountFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditForm ? (
        <EditAccountFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          accountId={selectedAccountId}
        />
      ) : null}
    </div>
  );
};

export default AccountListingWrapper;
